import React, { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, CircleDollarSign, Mic } from 'lucide-react';
import { useI3Modal } from '../context/I3ModalContext';
import { i3Assets, resolvePublicMediaSrc } from '../utils/i3Assets';
import { useRevealSections } from '../hooks/useReveal';
import { useI3SiteContent } from '../cms/useI3SiteContent';
import '../styles/I3Home.css';

const I3Home: React.FC = () => {
  const { openModal } = useI3Modal();
  const { content } = useI3SiteContent();
  const upcomingEvent = useMemo(() => {
    const next = content.eventsPage.upcomingEvents[0];
    if (next) {
      return {
        title: next.title,
        date: next.date,
        time: next.time,
        ctaLabel: next.registerUrl ? (next.ctaLabel ?? 'Register') : (next.ctaLabel ?? 'Details'),
      };
    }
    const ev = content.featuredEvent;
    return {
      title: ev.title,
      date: ev.date,
      time: ev.time,
      ctaLabel: 'Details',
    };
  }, [content.eventsPage.upcomingEvents, content.featuredEvent]);
  const homeRef = useRef<HTMLDivElement>(null);
  useRevealSections(homeRef);

  return (
    <div className="i3-home" ref={homeRef}>
      {/* Hero with roses video (same as intro) — flowers only here; CTAs slide in for smooth transition */}
      <section className="i3-hero i3-hero-with-video">
        <div className="i3-hero-backdrop" aria-hidden>
          <video
            className="i3-hero-backdrop-video"
            autoPlay
            muted
            loop
            playsInline
            aria-hidden
          >
            <source src="/videos/Roses.mp4" type="video/mp4" />
          </video>
          <div className="i3-hero-backdrop-scrim" aria-hidden />
        </div>
        <div className="i3-hero-content">
          <div className="i3-hero-logo-wrap" aria-hidden>
            <img src={i3Assets.logos.whiteTraced} alt="" className="i3-hero-logo-svg" />
          </div>
          <h1 className="i3-hero-headline">Welcome to I-3 Coaching & Consulting</h1>
          <div className="i3-hero-tagline i3-hero-tagline--i3words" aria-label="Impact, Influence, Inspire">
            <span className="i3-tagline-i3word i3-tagline-i3word-1">Impact</span>
            <span className="i3-tagline-i3word i3-tagline-i3word-2">Influence</span>
            <span className="i3-tagline-i3word i3-tagline-i3word-3">Inspire</span>
          </div>
          <div className="i3-hero-ctas i3-hero-ctas-slide-in">
            <Link to="/about" className="i3-cta i3-cta-primary">
              Start Your Journey
            </Link>
            <Link
              to="/services"
              className="i3-cta i3-cta-primary"
              aria-label="Book a session — choose a service and scheduling link"
            >
              Book a Session
            </Link>
          </div>
        </div>
      </section>

      {/* What We Do — four vertical pillars + shared View services */}
      <section className="i3-section i3-what-we-do i3-home-section-bleed" aria-labelledby="i3-what-we-do-title">
        <div className="i3-home-bleed-header">
          <p className="i3-what-we-do-eyebrow">Our pillars</p>
          <h2 id="i3-what-we-do-title" className="i3-what-we-do-headline">
            What we do
          </h2>
          <p className="i3-what-we-do-lead">
            Marriage, leadership, financial wellness, and speaking—designed to meet you where you are, with
            warmth and structure.
          </p>
        </div>
        <div className="i3-pillars-row">
          <article className="i3-pillar i3-pillar--marriage i3-reveal">
            <div className="i3-pillar-stripe" aria-hidden />
            <div className="i3-pillar-body">
              <span className="i3-pillar-index" aria-hidden>
                01
              </span>
              <span className="i3-pillar-icon" aria-hidden>
                <Heart size={28} strokeWidth={1.75} />
              </span>
              <h3>Marriage enrichment</h3>
              <p>Coaching and retreats that strengthen communication, connection, and covenant.</p>
              <button type="button" className="i3-pillar-learn i3-pillar-learn--green" onClick={() => openModal('marriage')}>
                Learn more
              </button>
            </div>
          </article>
          <article className="i3-pillar i3-pillar--leadership i3-reveal">
            <div className="i3-pillar-stripe" aria-hidden />
            <div className="i3-pillar-body">
              <span className="i3-pillar-index" aria-hidden>
                02
              </span>
              <span className="i3-pillar-icon" aria-hidden>
                <Sparkles size={28} strokeWidth={1.75} />
              </span>
              <h3>Leadership development</h3>
              <p>Grow in influence at home, at work, and in your community—with integrity and clarity.</p>
              <button type="button" className="i3-pillar-learn i3-pillar-learn--pink" onClick={() => openModal('leadership')}>
                Learn more
              </button>
            </div>
          </article>
          <article className="i3-pillar i3-pillar--finance i3-reveal">
            <div className="i3-pillar-stripe" aria-hidden />
            <div className="i3-pillar-body">
              <span className="i3-pillar-index" aria-hidden>
                03
              </span>
              <span className="i3-pillar-icon" aria-hidden>
                <CircleDollarSign size={28} strokeWidth={1.75} />
              </span>
              <h3>Financial wellness</h3>
              <p>Practical tools and coaching so money supports your values—not the other way around.</p>
              <button type="button" className="i3-pillar-learn i3-pillar-learn--green" onClick={() => openModal('financial')}>
                Learn more
              </button>
            </div>
          </article>
          <article className="i3-pillar i3-pillar--speaking i3-reveal">
            <div className="i3-pillar-stripe" aria-hidden />
            <div className="i3-pillar-body">
              <span className="i3-pillar-index" aria-hidden>
                04
              </span>
              <span className="i3-pillar-icon" aria-hidden>
                <Mic size={28} strokeWidth={1.75} />
              </span>
              <h3>Speaking engagements</h3>
              <p>Keynotes and workshops for churches, teams, and events—faith-centered and tailored to your audience.</p>
              <button type="button" className="i3-pillar-learn i3-pillar-learn--pink" onClick={() => openModal('speaking')}>
                Learn more
              </button>
            </div>
          </article>
        </div>
        <div className="i3-pillars-footer">
          <Link to="/services" className="i3-pillars-view-services">
            View services
          </Link>
        </div>
      </section>

      {/* Meet the Founders — full-width editorial split: copy + photography */}
      <section className="i3-section i3-founders i3-founders-band i3-home-section-bleed" aria-labelledby="i3-founders-title">
        <div className="i3-founders-inner">
          <div className="i3-founders-copy">
            <p className="i3-founders-kicker">The people behind I-3</p>
            <h2 id="i3-founders-title">Meet Troy &amp; Sharrea</h2>
            <p className="i3-founders-intro">
              Decades of leadership, ministry, and lived experience—for couples, individuals, and leaders
              who want to grow with purpose.
            </p>
            <ul className="i3-founders-highlights">
              <li>Marriage enrichment &amp; retreats</li>
              <li>Leadership &amp; financial wellness</li>
              <li>Faith-centered, practical guidance</li>
            </ul>
            <div className="i3-founders-actions">
              <Link to="/about" className="i3-cta i3-cta-primary">
                Read our story
              </Link>
              <Link to="/contact" className="i3-cta i3-cta-secondary">
                Get in touch
              </Link>
            </div>
          </div>
          <div className="i3-founders-visual i3-founders-visual--single">
            <figure className="i3-founders-figure i3-founders-figure--full">
              <img
                src={i3Assets.path('ThisisUs.png')}
                alt="Troy and Sharrea, founders of I-3 Coaching &amp; Consulting"
                className="i3-founders-photo-img"
                decoding="async"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </figure>
          </div>
        </div>
      </section>

      {/* Testimonials — editorial, full-width band */}
      <section
        className="i3-section i3-testimonials i3-testimonials-band i3-home-section-bleed"
        aria-labelledby="i3-testimonials-title"
      >
        <div className="i3-home-bleed-header i3-testimonials-header">
          <p className="i3-testimonials-kicker">Stories</p>
          <h2 id="i3-testimonials-title" className="i3-testimonials-title">
            What people are saying
          </h2>
          <p className="i3-testimonials-deck">
            Words from people we&apos;ve walked alongside—shared with permission.
          </p>
        </div>
        <div className="i3-testimonials-showcase">
          <article className="i3-testimonial-card i3-testimonial-card--premium i3-reveal">
            <div className="i3-testimonial-layout">
              <div className="i3-testimonial-visual">
                <div className="i3-testimonial-photo-frame">
                  <img
                    src={i3Assets.photos.kironDani}
                    alt="Kiron and Dani"
                    className="i3-testimonial-photo"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              </div>
              <blockquote className="i3-testimonial-quote i3-testimonial-quote--premium">
                <span className="i3-testimonial-mark" aria-hidden="true">
                  “
                </span>
                <p className="i3-testimonial-text">
                  As we prepare for marriage, we are intentional about building on a solid spiritual
                  foundation. Our marriage counselors, Troy and Sharrea, have played a pivotal role
                  in helping us structure our future marriage on the principles and foundation of
                  God&apos;s Word. Through their guidance, we&apos;ve learned that marriage is not
                  simply about love, but about covenant, accountability, servant leadership,
                  forgiveness, and daily surrender to Christ.
                </p>
                <footer className="i3-testimonial-footer">
                  <cite className="i3-testimonial-cite">Kiron &amp; Dani</cite>
                  <span className="i3-testimonial-role">Premarital coaching</span>
                </footer>
              </blockquote>
            </div>
          </article>
        </div>
      </section>

      {/* Events + journal — upcoming card sits tight under the section title */}
      <section
        className="i3-section i3-events-landing-band i3-home-section-bleed i3-reveal"
        aria-labelledby="i3-events-landing-title"
      >
        <div className="i3-events-landing-inner">
          <div className="i3-events-landing-upcoming">
            <p className="i3-events-landing-eyebrow">Upcoming</p>
            <p className="i3-events-landing-upcoming-title">{upcomingEvent.title}</p>
            {upcomingEvent.date || upcomingEvent.time ? (
              <p className="i3-events-landing-upcoming-meta">
                {[upcomingEvent.date, upcomingEvent.time].filter(Boolean).join(' · ')}
              </p>
            ) : null}
            <Link to="/events" className="i3-cta i3-cta-primary i3-events-landing-upcoming-cta">
              {upcomingEvent.ctaLabel}
            </Link>
          </div>

          <h2 id="i3-events-landing-title" className="i3-events-landing-title">
            Events &amp; gatherings
          </h2>
          <p className="i3-events-landing-copy">
            Photos, clips, and schedules live on Events; stories and longer reflections on the Journal.
          </p>

          <div className="i3-events-landing-ctas">
            <Link to="/events" className="i3-cta i3-cta-secondary">
              All events
            </Link>
            <Link to="/blog" className="i3-cta i3-cta-secondary">
              Journal
            </Link>
          </div>
        </div>
        {content.eventsPage.homeSpotlight?.image ? (
          <div
            className="i3-home-event-spotlight--in-events-band i3-reveal"
            aria-labelledby="i3-home-spotlight-title"
          >
            <div className="i3-home-event-spotlight-inner">
              <div className="i3-home-event-spotlight-figure">
                <img
                  src={resolvePublicMediaSrc(content.eventsPage.homeSpotlight.image)}
                  alt=""
                  className="i3-home-event-spotlight-img"
                />
              </div>
              <div className="i3-home-event-spotlight-copy">
                <h2 id="i3-home-spotlight-title" className="i3-home-event-spotlight-headline">
                  {content.eventsPage.homeSpotlight.headline ?? 'From our latest event'}
                </h2>
                {content.eventsPage.homeSpotlight.subline ? (
                  <p className="i3-home-event-spotlight-sub">{content.eventsPage.homeSpotlight.subline}</p>
                ) : null}
                <Link
                  to={content.eventsPage.homeSpotlight.linkTo ?? '/events'}
                  className="i3-cta i3-cta-primary i3-home-event-spotlight-cta"
                >
                  {content.eventsPage.homeSpotlight.linkLabel ?? 'Highlights'}
                </Link>
              </div>
            </div>
          </div>
        ) : null}
      </section>

    </div>
  );
};

export default I3Home;
